<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { getPalette } from '../../theme.svelte';
  import { showGlobalError } from '../../errorBanner.svelte';
  import {
    EFFORT_HELP_TEXT,
    findEffortOption,
    type EffortOption,
  } from '../effort';

  let {
    palette = getPalette(),
    busy = false,
    effort = null,
    effortOptions = [],
    onStop = undefined,
    onSend = undefined,         // (text: string) => void
    onSendAudio = undefined,    // (audioData: string, mimeType: string) => void
    onSendFiles = undefined,    // (files: File[], caption: string) => void
    onEffortChange = undefined, // (value: string) => void
  } = $props<{
    palette?: ReturnType<typeof getPalette>;
    busy?: boolean;
    /** Nivel de análisis efectivo del chat (o del default global). */
    effort?: string | null;
    /** Catálogo de niveles disponibles. */
    effortOptions?: EffortOption[];
    onStop?: () => void;
    onSend?: (text: string) => void;
    onSendAudio?: (audioData: string, mimeType: string) => void;
    onSendFiles?: (files: File[], caption: string) => void;
    onEffortChange?: (value: string) => void;
  }>();

  // ── Nivel de análisis (thinking effort) ─────────────────────
  // El cambio se guarda al elegir y aplica al SIGUIENTE mensaje, aunque la
  // respuesta actual siga en curso (por eso no se deshabilita con `busy`).
  let effortOpen = $state(false);

  const currentEffort = $derived(findEffortOption(effortOptions, effort ?? null));

  function toggleEffortPopover() {
    effortOpen = !effortOpen;
  }

  function chooseEffort(value: string) {
    effortOpen = false;
    if (!onEffortChange || value === effort) return;
    onEffortChange(value);
  }

  $effect(() => {
    if (!effortOpen) return;
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') effortOpen = false;
    };
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  });

  // ── Text input ──────────────────────────────────────────────
  let message = $state('');
  let textareaEl = $state<HTMLTextAreaElement | null>(null);
  const MIN_HEIGHT = 44;
  const MAX_HEIGHT = 140;
  let isComposing = false;

  // En móviles/tablets (pointer: coarse) Enter debe insertar un salto de
  // línea, no enviar: los usuarios usan el botón de enviar. En desktop se
  // mantiene Enter = enviar y Shift+Enter = salto de línea.
  // Ctrl/Cmd+Enter envía en cualquier dispositivo.
  let isCoarsePointer = $state(false);

  function autoResize() {
    if (!textareaEl) return;
    textareaEl.style.height = 'auto';
    const newHeight = Math.min(textareaEl.scrollHeight, MAX_HEIGHT);
    textareaEl.style.height = `${newHeight}px`;
    textareaEl.style.overflowY =
      textareaEl.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }

  function sendText() {
    const trimmed = message.trim();
    if (!trimmed) return;
    if (onSend) onSend(trimmed);
    message = '';
    autoResize();
  }

  function handleSubmit(event: Event) {
    event.preventDefault();
    if (busy) {
      if (onStop) onStop();
      return;
    }
    // If a file is selected, send file (with caption); otherwise send text
    if (selectedFiles.length > 0) {
      sendFilesWithCaption();
    } else {
      sendText();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey || isComposing) return;

    const forceSend = event.ctrlKey || event.metaKey;
    // En móvil, deja que Enter inserte el salto de línea nativo.
    if (isCoarsePointer && !forceSend) return;

    event.preventDefault();
    if (busy) {
      if (onStop) onStop();
    } else if (selectedFiles.length > 0) {
      sendFilesWithCaption();
    } else {
      sendText();
    }
  }

  // ── Audio recording ──────────────────────────────────────────
  type RecordingState = 'idle' | 'recording' | 'paused' | 'finalizing' | 'preview' | 'sending';
  let recordingState = $state<RecordingState>('idle');
  let recordingSeconds = $state(0);
  let mediaRecorder: MediaRecorder | null = null;
  let mediaStream: MediaStream | null = null;
  let audioChunks: Blob[] = [];
  let recordingTimer: ReturnType<typeof setInterval> | null = null;
  let micPermissionDenied = $state(false);
  let discardRecording = false;
  let audioPreviewBlob: Blob | null = null;
  let audioPreviewUrl = $state<string | null>(null);
  let audioPreviewMimeType = 'audio/webm';
  let audioPreviewEl = $state<HTMLAudioElement | null>(null);
  let previewPlaying = $state(false);
  let previewCurrentTime = $state(0);
  let previewDuration = $state(0);
  let pauseSupported = $state(true);

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function stopRecordingTimer() {
    if (recordingTimer) {
      clearInterval(recordingTimer);
      recordingTimer = null;
    }
  }

  function startRecordingTimer() {
    stopRecordingTimer();
    recordingTimer = setInterval(() => {
      recordingSeconds++;
    }, 1000);
  }

  function releaseMicrophone() {
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }

  function clearAudioPreview() {
    audioPreviewEl?.pause();
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    audioPreviewUrl = null;
    audioPreviewBlob = null;
    audioPreviewMimeType = 'audio/webm';
    audioPreviewEl = null;
    previewPlaying = false;
    previewCurrentTime = 0;
    previewDuration = 0;
  }

  async function togglePreviewPlayback() {
    if (!audioPreviewEl) return;
    if (audioPreviewEl.paused) {
      try {
        await audioPreviewEl.play();
        previewPlaying = true;
      } catch {
        showGlobalError('No fue posible reproducir la nota de voz.');
      }
    } else {
      audioPreviewEl.pause();
      previewPlaying = false;
    }
  }

  function updatePreviewMetadata() {
    if (!audioPreviewEl) return;
    previewDuration = Number.isFinite(audioPreviewEl.duration)
      ? audioPreviewEl.duration
      : recordingSeconds;
  }

  function updatePreviewTime() {
    if (audioPreviewEl) previewCurrentTime = audioPreviewEl.currentTime;
  }

  function seekAudioPreview(event: Event) {
    if (!audioPreviewEl) return;
    const input = event.currentTarget as HTMLInputElement;
    const nextTime = Number(input.value);
    audioPreviewEl.currentTime = nextTime;
    previewCurrentTime = nextTime;
  }

  function handlePreviewEnded() {
    previewPlaying = false;
    previewCurrentTime = 0;
    if (audioPreviewEl) audioPreviewEl.currentTime = 0;
  }

  function resetRecording() {
    stopRecordingTimer();
    releaseMicrophone();
    mediaRecorder = null;
    audioChunks = [];
    discardRecording = false;
    recordingSeconds = 0;
    recordingState = 'idle';
  }

  async function startRecording() {
    if (recordingState !== 'idle' || busy) return;
    micPermissionDenied = false;
    selectedFiles = [];
    clearAudioPreview();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream = stream;

      // Prefer webm; fall back to whatever is available
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
        else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else mimeType = ''; // let browser choose
      }

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorder = recorder;
      audioChunks = [];
      discardRecording = false;
      pauseSupported = typeof recorder.pause === 'function' && typeof recorder.resume === 'function';

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.push(e.data);
      };

      recorder.onstop = () => {
        releaseMicrophone();
        stopRecordingTimer();

        if (discardRecording) {
          resetRecording();
          return;
        }

        const finalMimeType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunks, { type: finalMimeType });
        mediaRecorder = null;

        if (blob.size === 0) {
          showGlobalError('La grabación quedó vacía. Intenta nuevamente.');
          resetRecording();
          return;
        }

        audioPreviewBlob = blob;
        audioPreviewMimeType = finalMimeType;
        audioPreviewUrl = URL.createObjectURL(blob);
        recordingState = 'preview';
      };

      recorder.onerror = () => {
        showGlobalError('Ocurrió un error mientras se grababa el audio.');
        discardRecording = true;
        resetRecording();
      };

      recorder.start(250);
      recordingState = 'recording';
      recordingSeconds = 0;
      startRecordingTimer();

    } catch (err) {
      console.error('Microphone access denied or not available:', err);
      micPermissionDenied = true;
      releaseMicrophone();
      recordingState = 'idle';
    }
  }

  function finishRecording() {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') return;
    recordingState = 'finalizing';
    stopRecordingTimer();
    mediaRecorder.stop();
  }

  function pauseRecording() {
    if (!mediaRecorder || mediaRecorder.state !== 'recording' || !pauseSupported) return;
    mediaRecorder.pause();
    recordingState = 'paused';
    stopRecordingTimer();
  }

  function resumeRecording() {
    if (!mediaRecorder || mediaRecorder.state !== 'paused') return;
    mediaRecorder.resume();
    recordingState = 'recording';
    startRecordingTimer();
  }

  function cancelRecording() {
    discardRecording = true;
    stopRecordingTimer();
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    } else {
      resetRecording();
    }
  }

  function discardAudioPreview() {
    clearAudioPreview();
    resetRecording();
  }

  function sendAudioPreview() {
    if (!audioPreviewBlob || !onSendAudio || recordingState !== 'preview') return;
    recordingState = 'sending';
    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      if (!base64) {
        showGlobalError('No fue posible preparar la nota de voz.');
        recordingState = 'preview';
        return;
      }

      onSendAudio(base64, audioPreviewMimeType);
      clearAudioPreview();
      resetRecording();
    };

    reader.onerror = () => {
      showGlobalError('No fue posible preparar la nota de voz.');
      recordingState = 'preview';
    };

    reader.readAsDataURL(audioPreviewBlob);
  }

  // ── File attachment ──────────────────────────────────────────
  let fileInputEl = $state<HTMLInputElement | null>(null);
  let selectedFiles = $state<File[]>([]);

  const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,image/webp,image/tiff,image/bmp,image/gif,application/pdf';
  const MAX_FILE_SIZE = 49 * 1024 * 1024; // 49 MB
  const MAX_FILES_PER_MESSAGE = 3;

  function determineMediaType(mimeType: string): 'audio' | 'image' | 'document' {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  }

  function openFilePicker() {
    fileInputEl?.click();
  }

  function addFiles(files: File[]) {
    const combined = [...selectedFiles, ...files];
    if (combined.length > MAX_FILES_PER_MESSAGE) {
      showGlobalError(`Puedes adjuntar máximo ${MAX_FILES_PER_MESSAGE} documentos por mensaje.`);
      return;
    }
    for (const file of files) {
      if (!ACCEPTED_FILE_TYPES.split(',').includes(file.type)) {
        showGlobalError(`Formato de ${file.name} no soportado.`);
        return;
      }
      if (file.size <= 0 || file.size > MAX_FILE_SIZE) {
        showGlobalError(`${file.name} excede el límite de 49 MB.`);
        return;
      }
    }
    selectedFiles = combined;
  }

  function handleFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    addFiles(Array.from(input.files ?? []));

    // Reset input so the same file can be re-selected
    input.value = '';
  }

  function removeSelectedFile(index: number) {
    selectedFiles = selectedFiles.filter((_, currentIndex) => currentIndex !== index);
  }

  function sendFilesWithCaption() {
    if (selectedFiles.length === 0 || !onSendFiles) return;

    const caption = message.trim();
    onSendFiles([...selectedFiles], caption);
    selectedFiles = [];
    message = '';
    autoResize();
  }

  // ── Paste handler (images/documents from clipboard) ───────
  function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Buscar una imagen o archivo en el portapapeles
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind === 'file') {
        // Imagen o documento pegado como archivo
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > MAX_FILE_SIZE) {
          showGlobalError('El archivo excede el límite de 49 MB. Por favor, selecciona un archivo más pequeño.');
          e.preventDefault();
          return;
        }

        e.preventDefault(); // evitar que pegue el archivo como texto/base64

        addFiles([file]);
        return;
      }

      // Algunos navegadores ponen imágenes como tipo 'string' con MIME image/*
      if (item.kind === 'string' && item.type.startsWith('image/')) {
        e.preventDefault();

        item.getAsString((content) => {
          // El contenido es la imagen en base64 (ya con data:image/...;base64,)
          fetch(content)
            .then(response => response.blob())
            .then(blob => addFiles([new File([blob], 'imagen.png', { type: item.type })]))
            .catch(() => showGlobalError('No fue posible preparar la imagen pegada.'));
        });
        return;
      }
    }
    // Si no hay imagen/archivo, deja que el paste normal (texto) ocurra
  }

  // ── Lifecycle ────────────────────────────────────────────────
  onMount(() => {
    autoResize();
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    }
  });

  onDestroy(() => {
    discardRecording = true;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    stopRecordingTimer();
    releaseMicrophone();
    clearAudioPreview();
  });
</script>

<form class="chat-input" onsubmit={handleSubmit}>
  <!-- Nivel de análisis (thinking effort): se guarda al elegir y aplica al
       siguiente mensaje, incluso si hay una respuesta en curso. -->
  {#if onEffortChange && effortOptions.length > 0}
    <div class="effort-bar">
      <button
        type="button"
        class="effort-trigger"
        class:open={effortOpen}
        aria-haspopup="true"
        aria-expanded={effortOpen}
        title={EFFORT_HELP_TEXT}
        onclick={toggleEffortPopover}
        style={`--effort-bg: ${palette.bgChatInput}; --effort-border: ${palette.borderSoft}; color: ${palette.textSecondary};`}
      >
        <i class="bi bi-lightning-charge effort-trigger-icon" style={`color: ${palette.accent};`} aria-hidden="true"></i>
        <span class="effort-trigger-label">Nivel de análisis:</span>
        <strong class="effort-trigger-value" style={`color: ${palette.textPrimary};`}>
          {currentEffort ? currentEffort.title : '—'}
        </strong>
        <i class="bi bi-chevron-up effort-trigger-caret" class:flip={effortOpen} aria-hidden="true"></i>
      </button>
    </div>

    {#if effortOpen}
      <button
        type="button"
        class="effort-backdrop"
        aria-label="Cerrar selector de nivel de análisis"
        onclick={() => { effortOpen = false; }}
        tabindex="-1"
      ></button>

      <div
        class="effort-popover"
        role="group"
        aria-label="Nivel de análisis"
        style={`background-color: ${palette.bgChatInput}; border-color: ${palette.borderSoft}; color: ${palette.textPrimary};`}
      >
        <p class="effort-intro" style={`color: ${palette.textSecondary};`}>
          Cuánto razona la IA antes de responder. Más análisis puede tardar más;
          no garantiza mayor precisión. Aplica al siguiente mensaje.
        </p>

        {#each effortOptions as option (option.value)}
          <button
            type="button"
            class="effort-option"
            class:selected={option.value === effort}
            aria-pressed={option.value === effort}
            onclick={() => chooseEffort(option.value)}
            style={`border-color: ${option.value === effort ? palette.accent : palette.borderSoft};`}
          >
            <span class="effort-option-check" style={`color: ${palette.accent};`}>
              <i class="bi {option.value === effort ? 'bi-check-circle-fill' : 'bi-circle'}"></i>
            </span>
            <span class="effort-option-body">
              <span class="effort-option-head">
                <strong style={`color: ${palette.textPrimary};`}>{option.title}</strong>
                <span
                  class="effort-option-badge"
                  style={`background-color: ${palette.bgCard}; color: ${palette.textSecondary}; border-color: ${palette.borderSoft};`}
                >{option.label}</span>
              </span>
              <span class="effort-option-summary" style={`color: ${palette.textSecondary};`}>
                {option.summary}
              </span>
            </span>
          </button>
        {/each}
        <details class="effort-details" style={`color: ${palette.textSecondary};`}>
          <summary>Qué significa cada nivel</summary>
          {#each effortOptions as option (option.value)}
            <p class="effort-option-desc"><strong>{option.title}:</strong> {option.description}</p>
          {/each}
        </details>
      </div>
    {/if}
  {/if}

  <!-- File attachment chip -->
  {#if selectedFiles.length > 0}
    <div class="file-chips">
      {#each selectedFiles as file, index (`${file.name}-${file.size}-${index}`)}
        <div class="file-chip" style={`background-color: ${palette.bgChatInput}; color: ${palette.textPrimary}; border: 1px solid ${palette.borderSoft || 'rgba(0,0,0,0.1)'};`}>
          <span class="file-chip-icon bi {determineMediaType(file.type) === 'image' ? 'bi-file-image' : 'bi-file-earmark-pdf'}"></span>
          <span class="file-chip-name">{file.name}</span>
          <button
            type="button"
            class="file-chip-remove bi bi-x-circle"
            onclick={() => removeSelectedFile(index)}
            title="Quitar archivo"
            style={`color: ${palette.textSecondary};`}
          ></button>
        </div>
      {/each}
    </div>
  {/if}

  <div
    class="input-wrapper"
    style={`background-color: ${palette.bgChatInput}; color: ${palette.textPrimary};`}
  >
    {#if recordingState === 'recording' || recordingState === 'paused' || recordingState === 'finalizing'}
      <div class="recording-panel">
        <button
          type="button"
          class="audio-control danger"
          onclick={cancelRecording}
          title="Cancelar grabación"
          aria-label="Cancelar grabación"
          disabled={recordingState === 'finalizing'}
        >
          <i class="bi bi-trash3"></i>
        </button>

        <div class="recording-status" aria-live="polite">
          {#if recordingState === 'recording'}
            <span class="recording-dot"></span>
          {:else if recordingState === 'paused'}
            <i class="bi bi-pause-circle paused-icon"></i>
          {:else}
            <span class="mini-spinner"></span>
          {/if}
          <span class="recording-time">{formatTime(recordingSeconds)}</span>
        </div>

        <div class="recording-actions">
          {#if pauseSupported && recordingState !== 'finalizing'}
            <button
              type="button"
              class="audio-control"
              onclick={recordingState === 'paused' ? resumeRecording : pauseRecording}
              title={recordingState === 'paused' ? 'Continuar grabación' : 'Pausar grabación'}
              aria-label={recordingState === 'paused' ? 'Continuar grabación' : 'Pausar grabación'}
            >
              <i class="bi {recordingState === 'paused' ? 'bi-play-fill' : 'bi-pause-fill'}"></i>
            </button>
          {/if}

          <button
            type="button"
            class="audio-control finish"
            onclick={finishRecording}
            title="Terminar y revisar"
            aria-label="Terminar y revisar grabación"
            disabled={recordingState === 'finalizing'}
          >
            <i class="bi bi-check-lg"></i>
          </button>
        </div>
      </div>
    {:else if recordingState === 'preview' || recordingState === 'sending'}
      <div class="audio-preview-panel">
        <button
          type="button"
          class="audio-control danger"
          onclick={discardAudioPreview}
          title="Eliminar nota de voz"
          aria-label="Eliminar nota de voz"
          disabled={recordingState === 'sending'}
        >
          <i class="bi bi-trash3"></i>
        </button>

        {#if audioPreviewUrl}
          <audio
            class="audio-preview-native"
            bind:this={audioPreviewEl}
            src={audioPreviewUrl}
            preload="metadata"
            aria-label="Vista previa de la nota de voz"
            onloadedmetadata={updatePreviewMetadata}
            ontimeupdate={updatePreviewTime}
            onended={handlePreviewEnded}
          ></audio>

          <button
            type="button"
            class="audio-control preview-play"
            onclick={togglePreviewPlayback}
            title={previewPlaying ? 'Pausar reproducción' : 'Escuchar nota de voz'}
            aria-label={previewPlaying ? 'Pausar reproducción' : 'Escuchar nota de voz'}
          >
            <i class="bi {previewPlaying ? 'bi-pause-fill' : 'bi-play-fill'}"></i>
          </button>

          <div class="preview-track">
            <input
              class="audio-progress"
              type="range"
              min="0"
              max={previewDuration || recordingSeconds || 1}
              step="0.1"
              value={previewCurrentTime}
              oninput={seekAudioPreview}
              aria-label="Posición de reproducción"
              style={`--audio-progress: ${
                ((previewCurrentTime / (previewDuration || recordingSeconds || 1)) * 100).toFixed(2)
              }%; --accent: ${palette.accent};`}
            />
            <span class="preview-time">
              {formatTime(Math.floor(previewCurrentTime || previewDuration || recordingSeconds))}
            </span>
          </div>
        {/if}

        <button
          type="button"
          class="audio-control send-audio"
          onclick={sendAudioPreview}
          title="Enviar nota de voz"
          aria-label="Enviar nota de voz"
          disabled={recordingState === 'sending'}
          style={`background-color: ${palette.accent};`}
        >
          {#if recordingState === 'sending'}
            <span class="mini-spinner light"></span>
          {:else}
            <i class="bi bi-send-fill"></i>
          {/if}
        </button>
      </div>
    {:else}
      <!-- Attachment button (only if callback provided) -->
      {#if onSendFiles}
      <button
        type="button"
        class="attach-btn"
        onclick={openFilePicker}
        title="Adjuntar archivo"
        style={`color: ${palette.textSecondary};`}
        disabled={busy}
      >
        <i class="bi bi-paperclip"></i>
      </button>

      <input
        type="file"
        bind:this={fileInputEl}
        class="file-input-hidden"
        accept={ACCEPTED_FILE_TYPES}
        multiple
        onchange={handleFileSelected}
      />
      {/if}

    <!-- Textarea -->
    <textarea
      bind:this={textareaEl}
      class="textarea"
      rows="1"
      placeholder={selectedFiles.length > 0 ? 'Añadir un mensaje (opcional)...' : 'Enviar un mensaje...'}
      bind:value={message}
      oninput={autoResize}
      onkeydown={handleKeydown}
      onpaste={handlePaste}
      oncompositionstart={() => (isComposing = true)}
      oncompositionend={() => (isComposing = false)}
      enterkeyhint="enter"
      style={`color: ${palette.textPrimary};`}
    ></textarea>

    <!-- Microphone button (only if callback provided) -->
    {#if onSendAudio}
      <button
        type="button"
        class="mic-btn"
        onclick={startRecording}
        title="Grabar nota de voz"
        aria-label="Grabar nota de voz"
        style={`color: ${palette.textSecondary};`}
        disabled={busy}
      >
        <i class="bi bi-mic-fill"></i>
      </button>
    {/if}

    <!-- Send / Stop button -->
    {#if busy}
      <button
        class="send-btn bi bi-stop-circle"
        type="submit"
        title="Detener respuesta"
        style={`color: ${palette.accent}; --hover-color: ${palette.accent};`}
      ></button>
    {:else}
      <button
        class="send-btn bi bi-arrow-up-circle-fill"
        type="submit"
        title="Enviar"
        style={`color: ${palette.textPrimary}; --hover-color: ${palette.accent};`}
      disabled={!message.trim() && selectedFiles.length === 0}
      ></button>
    {/if}
    {/if}
  </div>

  <!-- Mic permission denied message -->
  {#if micPermissionDenied}
    <div class="mic-denied" style={`color: ${palette.textSecondary};`}>
      <i class="bi bi-exclamation-triangle-fill"></i>
      Acceso al micrófono denegado. Permite el acceso en la configuración del navegador.
    </div>
  {/if}
</form>

<style>
  .chat-input {
    max-width: 720px;
    width: 100%;
    min-width: 0;
    margin: 0 auto;
    position: relative; /* ancla del popover de nivel de análisis */
  }

  /* ── Nivel de análisis ───────────────────────────── */
  .effort-bar {
    display: flex;
    justify-content: flex-start;
    padding: 0 6px 6px;
  }

  .effort-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--effort-border, transparent);
    background-color: var(--effort-bg, transparent);
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }

  .effort-trigger:hover,
  .effort-trigger.open {
    background-color: rgba(128, 128, 128, 0.12);
  }

  .effort-trigger-label {
    opacity: 0.85;
  }

  .effort-trigger-value {
    font-weight: 600;
  }

  .effort-trigger-icon {
    font-size: 0.85rem;
  }

  .effort-trigger-caret {
    font-size: 0.7rem;
    transition: transform 0.15s ease;
  }

  .effort-trigger-caret.flip {
    transform: rotate(180deg);
  }

  /* Backdrop invisible: cierra el popover al hacer clic fuera. */
  .effort-backdrop {
    position: fixed;
    inset: 0;
    z-index: 40;
    background: transparent;
    border: none;
    padding: 0;
    cursor: default;
  }

  .effort-popover {
    position: absolute;
    z-index: 41;
    bottom: calc(100% + 6px);
    left: 6px;
    right: 6px;
    max-height: min(66vh, 520px);
    overflow-y: auto;
    box-sizing: border-box;
    border: 1px solid;
    border-radius: 14px;
    padding: 10px;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    text-align: left;
  }

  .effort-intro {
    margin: 0 0 8px;
    font-size: 12px;
    line-height: 1.45;
  }

  .effort-option {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 1px solid;
    border-radius: 10px;
    padding: 7px 9px;
    margin-bottom: 6px;
    cursor: pointer;
    font-size: 12px;
    transition: border-color 0.15s ease, background-color 0.15s ease;
  }

  .effort-option:last-child {
    margin-bottom: 0;
  }

  .effort-option:hover {
    background-color: rgba(128, 128, 128, 0.08);
  }

  .effort-option-check {
    font-size: 0.95rem;
    line-height: 1.3;
  }

  .effort-option-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .effort-option-head {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .effort-option-badge {
    border: 1px solid;
    border-radius: 999px;
    padding: 0 6px;
    font-size: 10px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .effort-option-summary {
    font-size: 12px;
  }

  .effort-option-desc {
    font-size: 11px;
    line-height: 1.45;
    opacity: 0.9;
  }

  .effort-details {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.45;
  }

  .effort-details summary {
    cursor: pointer;
  }

  .input-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    border-radius: 16px;
    padding: 8px 10px 8px 8px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
    border: none;
    width: 100%;
    min-width: 0;
  }

  .attach-btn,
  .mic-btn {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 6px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background-color 0.15s ease;
    flex-shrink: 0;
  }

  .attach-btn:hover,
  .mic-btn:hover {
    background-color: rgba(128, 128, 128, 0.12);
  }

  .attach-btn:disabled,
  .mic-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .file-input-hidden {
    display: none;
  }

  .textarea {
    flex: 1;
    width: 100%;
    min-width: 0;
    resize: none;
    border: none;
    outline: none;
    font-size: 14px;
    line-height: 1.4;
    min-height: 44px;
    max-height: 140px;
    background: transparent;
    padding: 0;
    overflow-wrap: anywhere;
  }

  .textarea::placeholder {
    opacity: 0.6;
  }

  .textarea::-webkit-scrollbar {
    width: 6px;
  }

  .send-btn {
    background: none;
    border-width: 0;
    font-size: large;
    cursor: pointer;
    flex-shrink: 0;
    padding: 4px 2px;
  }

  .send-btn:hover::before {
    color: var(--hover-color);
  }

  .send-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ── Voice note recording / preview ──────────────── */
  .recording-panel,
  .audio-preview-panel {
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
  }

  .recording-status {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 4px;
  }

  .recording-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .audio-control {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 10px;
    background: rgba(128, 128, 128, 0.1);
    color: inherit;
    font-size: 17px;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  }

  .audio-control:hover:not(:disabled) {
    background: rgba(128, 128, 128, 0.18);
    transform: translateY(-1px);
  }

  .audio-control:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .audio-control.danger {
    color: #ef4444;
  }

  .audio-control.finish {
    color: #22c55e;
  }

  .audio-control.send-audio {
    color: #fff;
  }

  .audio-preview-native {
    display: none;
  }

  .preview-play {
    color: inherit;
  }

  .preview-track {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .audio-progress {
    flex: 1;
    min-width: 40px;
    height: 4px;
    margin: 0;
    border: 0;
    border-radius: 999px;
    appearance: none;
    background: linear-gradient(
      to right,
      var(--accent) 0 var(--audio-progress),
      rgba(128, 128, 128, 0.22) var(--audio-progress) 100%
    );
    cursor: pointer;
  }

  .audio-progress::-webkit-slider-thumb {
    width: 11px;
    height: 11px;
    appearance: none;
    border: 0;
    border-radius: 50%;
    background: var(--accent);
  }

  .audio-progress::-moz-range-thumb {
    width: 11px;
    height: 11px;
    border: 0;
    border-radius: 50%;
    background: var(--accent);
  }

  .preview-time {
    min-width: 31px;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    opacity: 0.68;
    text-align: right;
  }

  .paused-icon {
    color: #f59e0b;
    font-size: 16px;
  }

  .recording-dot {
    width: 9px;
    height: 9px;
    flex: 0 0 auto;
    border-radius: 50%;
    background-color: #ef4444;
    animation: pulse-dot 1s ease-in-out infinite;
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.3); }
  }

  .recording-time {
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    min-width: 38px;
  }

  .mini-spinner {
    width: 14px;
    height: 14px;
    display: inline-block;
    border: 2px solid rgba(128, 128, 128, 0.28);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: spin-audio 0.75s linear infinite;
  }

  .mini-spinner.light {
    border-color: rgba(255, 255, 255, 0.38);
    border-top-color: #fff;
  }

  @keyframes spin-audio {
    to { transform: rotate(360deg); }
  }

  /* ── File chip ───────────────────────────────────── */
  .file-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 10px;
    margin-bottom: 8px;
    font-size: 13px;
    max-width: 100%;
  }

  .file-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }

  .file-chips .file-chip {
    margin-bottom: 0;
    max-width: min(100%, 260px);
  }

  .file-chip-icon {
    font-size: 1rem;
    flex-shrink: 0;
  }

  .file-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-chip-remove {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-size: 1rem;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .file-chip-remove:hover {
    opacity: 0.7;
  }

  /* ── Mic denied ──────────────────────────────────── */
  .mic-denied {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    margin-top: 6px;
    padding: 0 4px;
  }

  @media (max-width: 768px) {
    .textarea {
      /* Prevent iOS Safari from zooming the page when focusing the field. */
      font-size: 16px;
    }

    .input-wrapper {
      gap: 4px;
      padding-left: 6px;
      padding-right: 8px;
    }

    .recording-panel,
    .audio-preview-panel {
      gap: 5px;
    }

    .audio-control {
      width: 34px;
      height: 34px;
      flex-basis: 34px;
    }

  }
</style>
